import React from "react";
import { useUserTheme } from "src/shared/utils/useUserTheme";
import { useUser } from "src/shared/context/UserContext";

interface YearSelectorProps {
  selectedYear: number;
  onYearChange: (increment: number) => void;
}

const YearSelector: React.FC<YearSelectorProps> = ({ selectedYear, onYearChange }) => {
  const { fontClass, borderHighlight } = useUserTheme();
  const { user } = useUser();

  // Get user's creation year
  const getCreationYear = () => {
    // Check both possible locations for createdOn
    if (user?.createdOn) {
      return new Date(user.createdOn).getFullYear();
    }
    return 2020; // fallback
  };

  const creationYear = getCreationYear();
  const currentYear = new Date().getFullYear();

  // Check if previous year is allowed (not before creation year)
  const canGoPrevious = selectedYear > creationYear;

  // Check if next year is allowed (not after current year)
  const canGoNext = selectedYear < currentYear;

  const handlePrevious = () => {
    if (canGoPrevious) {
      onYearChange(-1);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      onYearChange(1);
    }
  };

  return (
    <div className="flex items-center rounded-full px-4 py-1 border-2 transition-all duration-300" style={{ borderColor: borderHighlight, backgroundColor: "#f9fafb" }}>
      {/* Previous year */}
      <button
        onClick={handlePrevious}
        disabled={!canGoPrevious}
        className={`text-xl font-bold transition-transform duration-200 ${canGoPrevious ? "text-gray-600 hover:text-gray-900 hover:scale-110 cursor-pointer" : "text-gray-300 cursor-not-allowed"}`}
        aria-label="Previous year"
      >
        &lt;
      </button>

      {/* Current year */}
      <span className={`mx-4 font-bold text-lg px-3 py-1 rounded-full border-2 transition-all duration-300 ${fontClass}`} style={{ borderColor: borderHighlight, backgroundColor: "white" }}>
        {selectedYear}
      </span>

      {/* Next year */}
      <button
        onClick={handleNext}
        disabled={!canGoNext}
        className={`text-xl font-bold transition-transform duration-200 ${canGoNext ? "text-gray-600 hover:text-gray-900 hover:scale-110 cursor-pointer" : "text-gray-300 cursor-not-allowed"}`}
        aria-label="Next year"
      >
        &gt;
      </button>
    </div>
  );
};

export default YearSelector;
